import { ChangeDetectionStrategy, Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl, ControlValueAccessor, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR,
  ValidationErrors, Validator
} from '@angular/forms';
import { cloneDeep as _cloneDeep } from 'lodash-es';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LANGUAGES } from 'core/languages';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { Language } from 'generated/graphql';
import { isNilOrEmpty } from 'helpers';

export interface LanguageArrayItem {
  languageCode: Language;
  preferred: boolean;
}
export type LanguageFormModel = LanguageArrayItem[];

@Component({
  selector: 'app-language-form',
  templateUrl: './language-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LanguageFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => LanguageFormComponent),
      multi: true
    }
  ]
})
export class LanguageFormComponent
  extends WithDisposableSubject()
  implements ControlValueAccessor, Validator, OnInit, OnDestroy {

  // Internal typed form used by the template
  formGroup: FormGroup<{
    preferredLanguage: FormControl<Language | null>;
    entityLanguages: FormControl<Language[]>;
  }> = new FormGroup({
    preferredLanguage: new FormControl<Language | null>(null),
    entityLanguages: new FormControl<Language[]>([])
  });

  formOptions = {
    languages: _cloneDeep(LANGUAGES)
  };

  /** control for the filter keyword for 'preferred languages' */
  public preferredLanguageFilterCtrl: FormControl = new FormControl();

  /** control for the filter keyword for 'spoken languages' */
  public languageFilterCtrl: FormControl = new FormControl();

  /** list of preferred languages filtered by search keyword */
  public filteredPreferredLanguages: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);

  /** list of spoken languages filtered by search keyword */
  public filteredLanguages: ReplaySubject<any[]> = new ReplaySubject<any[]>(1);


  constructor() {
    super();

    // Keep preferred always included in the multi-select
    this.formGroup.controls.preferredLanguage.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe(preferred => {
        const current = this.formGroup.controls.entityLanguages.value ?? [];
        if (preferred && !current.includes(preferred)) {
          this.formGroup.controls.entityLanguages.setValue([...current, preferred]);
        }
      });

    // Guard against removing preferred from the multi-select
    this.formGroup.controls.entityLanguages.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe(list => {
        const preferred = this.formGroup.controls.preferredLanguage.value;
        if (preferred && list && !list.includes(preferred)) {
          // Re-add preferred silently (preferred change already emitted)
          this.formGroup.controls.entityLanguages.setValue([...list, preferred], { emitEvent: false });
        }
      });
  }

  ngOnInit() {
    // set initial selection
    this.filteredPreferredLanguages.next(this.formOptions.languages.slice());
    this.filteredLanguages.next(this.formOptions.languages.slice());

    // listen for search field value changes
    this.preferredLanguageFilterCtrl.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe(() => {
        this.filterPreferredLanguages();
      });

    this.languageFilterCtrl.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe(() => {
        this.filterLanguages();
      });
  }

  private filterPreferredLanguages() {
    if (!this.formOptions.languages) {
      return;
    }
    // get the search keyword
    let search = this.preferredLanguageFilterCtrl.value;

    if (!search) {
      this.filteredPreferredLanguages.next(this.formOptions.languages.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    // filter the languages
    const filtered = this.formOptions.languages.filter(lang => lang.text.toLowerCase().indexOf(search) > -1);

    this.filteredPreferredLanguages.next(filtered);
  }

  private filterLanguages() {
    if (!this.formOptions.languages) {
      return;
    }
    // get the search keyword
    let search = this.languageFilterCtrl.value;
    if (!search) {
      this.filteredLanguages.next(this.formOptions.languages.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the languages
    this.filteredLanguages.next(
      this.formOptions.languages.filter(lang => lang.text.toLowerCase().indexOf(search) > -1)
    );
  }

  // Factory for callers (dialog) to map entity -> control value
  static toFormModel(entity: { entityLanguages?: { languageCode: Language; preferred: boolean }[] }): LanguageFormModel {
    // Default value
    const list = !isNilOrEmpty(entity?.entityLanguages) ? entity.entityLanguages : [{ languageCode: Language.En, preferred: true }];
    return list.map(({ languageCode, preferred }) => ({
      languageCode,
      preferred: !!preferred
    }));
  }

  // CVA: write external value into internal control(s)
  writeValue(value: LanguageFormModel | null): void {
    const items = !isNilOrEmpty(value) ? value : [{ languageCode: Language.En, preferred: true }];
    const codes = items.map(x => x.languageCode);
    const preferred = items.find(x => x.preferred)?.languageCode ?? codes[0] ?? null;

    // Ensure preferred is present in the list
    const finalCodes = preferred ? Array.from(new Set([preferred, ...codes])) : codes;

    this.formGroup.controls.preferredLanguage.setValue(preferred, { emitEvent: false });
    this.formGroup.controls.entityLanguages.setValue(finalCodes, { emitEvent: false });

    // If we just applied a default value, we need to propagate it back up
    if (isNilOrEmpty(value)) {
      this._onChange(this.getValue());
    }
  }


  // CVA: propagate internal changes as the external value
  registerOnChange(fn: (value: LanguageFormModel) => void): void {
    this.formGroup.valueChanges
      .pipe(takeUntil(this.disposer))
      .subscribe(v => {
        const preferred = v.preferredLanguage ?? null;
        const codes = v.entityLanguages ?? [];
        // Ensure preferred is included
        const unique = preferred ? Array.from(new Set<Language>([preferred, ...codes])) : codes;

        const out: LanguageFormModel = unique.map(code => ({
          languageCode: code,
          preferred: preferred === code
        }));

        fn(out);
      });
  }

  _onChange = (_: LanguageFormModel) => { };
  _onTouched = () => { };
  registerOnTouched(fn: any): void { this._onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) this.formGroup.disable(); else this.formGroup.enable();
  }

  // Validator: ensure preferredLanguage is set and is included in the list
  validate(_: AbstractControl): ValidationErrors | null {
    const preferred = this.formGroup.controls.preferredLanguage.value;
    const list = this.formGroup.controls.entityLanguages.value ?? [];
    if (preferred && list.includes(preferred)) return null;
    return { language: true };
  }

  registerOnValidatorChange(fn: () => void): void {
    this.formGroup.valueChanges.pipe(takeUntil(this.disposer)).subscribe(() => fn());
  }

  // Helper if needed elsewhere
  getValue(): LanguageFormModel {
    const preferred = this.formGroup.controls.preferredLanguage.value;
    const codes = this.formGroup.controls.entityLanguages.value ?? [];
    const unique = preferred ? Array.from(new Set<Language>([preferred, ...codes])) : codes;
    return unique.map(code => ({
      languageCode: code,
      preferred: preferred === code
    }));
  }

  getLanguageSelectionDisplay(selected: Language[]): string {
    if (!selected || selected.length === 0) {
      return '';
    }

    const languageNames = selected.map(c => this.getLanguageDisplay(c));

    if (languageNames.length <= 2) {
      return languageNames.join(', ');
    }

    const others = languageNames.length - 2;
    return `${languageNames.slice(0, 2).join(', ')} +${others} more`;
  }

  getLanguageDisplay(code: Language): string {
    if (!code) {
      return '';
    }
    const lang = this.formOptions.languages.find(l => l.value === code);
    return lang ? lang.text : '';
  }
}
